import { Component, ChangeDetectionStrategy, input, computed, OnInit, inject } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Estimate, VendorOrder } from '../../../../../../shared/models/vendor-order.model';
import { VendorService } from '../../../../../../shared/services/vendor.service';
import { QuoteSowModalComponent, SowModalData } from '../quote-sow-modal/quote-sow-modal.component';

@Component({
  selector: 'app-quote-progress',
  imports: [CurrencyPipe],
  templateUrl: './quote-progress.component.html',
  styleUrl: './quote-progress.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteProgressComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly vendorService = inject(VendorService);

  readonly order = input.required<VendorOrder>();

  ngOnInit(): void {
    console.log('[QuoteProgress] order input:', this.order());
  }

  readonly vendorEstimate = computed<Estimate | null>(() => {
    const o = this.order();
    const estimates = o.estimates ?? [];

    // Match by vendorId if available
    if (o.vendorId) {
      const match = estimates.find(e =>
        o.vendorId === e.vendorId ||
        o.vendorId === e.workflow_uid ||
        o.vendorId === e.estimationsId
      );
      if (match) return match;
    }

    // Fall back to the estimate with an accepted quote
    const accepted = estimates.find(e =>
      e.quote?.acceptedBy && e.quote?.acceptedDate
    );
    if (accepted) return accepted;

    // Fall back to the single estimate if there's only one
    return estimates.length === 1 ? estimates[0] : null;
  });

  readonly vendorName = computed(() => this.vendorEstimate()?.name ?? 'Unknown Vendor');

  readonly quote = computed(() => {
    const est = this.vendorEstimate();
    if (!est) return null;
    return est.quote?.normal ?? est.quote?.rushed ?? null;
  });

  readonly deliveryType = computed(() => {
    const est = this.vendorEstimate();
    if (!est) return 'Standard';
    return (est.quote?.rushed && !est.quote?.normal) ? 'Rushed' : 'Standard';
  });

  readonly deliveryDate = computed(() => {
    const est = this.vendorEstimate();
    if (!est) return '--';
    const type = this.deliveryType();
    const raw = type === 'Rushed'
      ? est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
      : est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;
    return raw ? this.formatDate(raw) : (est.deadline ? this.formatDate(est.deadline) : '--');
  });

  readonly quoteId = computed(() => this.order().jobNumber ?? '--');

  readonly currency = computed(() => {
    const est = this.vendorEstimate();
    return est?.currency ?? this.quote()?.currency ?? 'USD';
  });

  readonly costBreakdown = computed<{ concept: string; cost: number }[]>(() => {
    const q = this.quote();
    if (!q) return [];
    if (q.costs?.length) return q.costs;
    const items: { concept: string; cost: number }[] = [];
    if (q.dtpCost) items.push({ concept: 'Desktop publishing', cost: q.dtpCost });
    if (q.translationCost) items.push({ concept: 'File preparation', cost: q.translationCost });
    if (q.managementCost) items.push({ concept: 'Management', cost: q.managementCost });
    if (q.rushedFees) items.push({ concept: 'Rushed fees', cost: q.rushedFees });
    return items;
  });

  readonly totalCost = computed(() => {
    const q = this.quote();
    if (q?.totalCost != null) return q.totalCost;
    const est = this.vendorEstimate();
    return est?.priceMax ?? est?.priceMin ?? 0;
  });

  readonly recommendedPOTotal = computed(() => {
    return Math.ceil(this.totalCost() * 1.15 * 100) / 100;
  });

  readonly purchaseOrderId = computed(() => this.order().vendorPrice ?? this.order().jobNumber ?? '--');



  async openSowModal(): Promise<void> {
    const order = this.order();
    const estimate = this.vendorEstimate();
    if (!estimate) return;

    let breakdown;
    try {
      const targetLocale = Array.isArray(order.targetLocaleId)
        ? order.targetLocaleId.join(',')
        : (order.targetLocaleId ?? '');
      breakdown = order.jobCreatedIn === 'xtm'
        ? await this.vendorService.getXtmCostBreakdown(order.jobUid ?? '', estimate.costId ?? '', estimate.costBreakdownId ?? '')
        : await this.vendorService.getEstimateReportBreakdown(
            order.contentType ?? '',
            order.sourceLocaleId ?? '',
            targetLocale,
            estimate.estimationsId ?? '',
          );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    const modalData: SowModalData = { order, estimate, breakdown, vendor };

    this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '95vh',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }
}
